import { apiService } from './ApiService';
import { AppEvent } from '../models/AppEvent';
import { Store } from '../wrappers';
/**
 * Reliability backup poll — the third transport under LAN + WAN.
 *
 * Re-fetches AppEvents this terminal may have missed on the best-effort live
 * transports (`GET /app-events`) and re-feeds them through the *unchanged*
 * AppEvent.handle() funnel, reusing its dedup + dispatch. A replayed event is
 * handled exactly like a delayed live copy — no special-casing.
 *
 * Mirrors OrdersHubService: server-clock watermark + single-flight. The poller
 * lifecycle and the >1-terminal gate live in useAppEventBackupPoll. See
 * docs/AppEventBackupPoll.md.
 */
// Single-flight: a scheduled tick and a reconnect re-poll can overlap.
let inFlight = null;
// Watermark = the server's clock (the `next_since` of the previous poll), in ms,
// never a locally-derived time. Module-scoped, so it must be reset on tenant
// switch / logout (see reset()) or the next session would carry a stale clock.
let lastWatermarkMs = null;
async function pollExec() {
    const terminal = Store.get('terminal');
    const params = { terminal: terminal?.key ?? '' };
    if (lastWatermarkMs !== null)
        params.since = String(lastWatermarkMs);
    try {
        const response = await apiService.get('/app-events', { params });
        const { next_since, events } = response.data;
        // Feed in created_at ASC (the endpoint returns ascending) through the
        // same handle() the live transports use — dedup + dispatch are identical.
        for (const event of events ?? []) {
            await AppEvent.handle(event);
        }
        if (typeof next_since === 'number')
            lastWatermarkMs = next_since;
    }
    catch {
        // Best-effort backstop: a failed poll never throws into the interval.
    }
}
const AppEventBackupService = {
    /**
     * Fetch and replay missed AppEvents. No-op when offline or without a
     * logged-in tenant. The caller (useAppEventBackupPoll) applies the
     * >1-terminal gate before calling this.
     */
    async poll() {
        if (!Store.get('isOnline') || !Store.get('tenant'))
            return;
        if (inFlight)
            return inFlight;
        inFlight = pollExec();
        try {
            await inFlight;
        }
        finally {
            inFlight = null;
        }
    },
    /**
     * Drop the watermark on logout / tenant switch so the next session starts
     * from a clean cold start (the module scope persists across re-login).
     */
    reset() {
        lastWatermarkMs = null;
        inFlight = null;
    },
};
export default AppEventBackupService;
export { AppEventBackupService };
//# sourceMappingURL=AppEventBackupService.js.map